<script lang="ts">
  import { capitalize } from 'lodash'
  import { onMount } from 'svelte'
  import couponsStore from '@/stores/coupons.svelte'
  import Portal from 'svelte-portal'
  import keywordsStore, { baseKeyword } from '@/stores/keywords.svelte'
  import { object, string } from 'yup'
  import LinkifyingTextarea from '../../shared/LinkifyingTextarea.svelte'
  import AttachCouponsList from '../../shared/AttachCouponsList.svelte'
  import LinkifyingPhonePreview from '../../shared/LinkifyingPhonePreview.svelte'
  import { fade } from 'svelte/transition'
  import AddToGroupForm from './AddToGroupForm.svelte'
  import GlobalErrors from '../../shared/GlobalErrors.svelte'
  import SuperadminAccordion from '../../shared/SuperadminAccordion.svelte'
  import AutoResponderWidget from './AutoResponderWidget.svelte'
  import AiResponderForm from './AiResponderForm.svelte'
  import SkipOptIns from './SkipOptIns.svelte'
  import globalStore from '@/stores/global.svelte'
  import { scrollToTop } from '@/utils'

  let {
    initialKeyword,
    aiResponderEnabled = false,
    exclusiveGroups = false,
  } = $props()

  // "How to use" reflects what's saved, not unsaved edits in the form below
  const savedKeyword = $derived(JSON.parse(initialKeyword))

  let keywordSchema = object({
    keyword: string().required('Keyword is required'),
  })

  let selectedGroups = $state([])
  let formErrors = $state([])
  let showingAddGroupsModal = $state(false)
  let currentKeyword = $state(baseKeyword)

  onMount(async () => {
    await couponsStore.fetch()

    currentKeyword = JSON.parse(initialKeyword)
    selectedGroups = currentKeyword.add_to_groups.map(step => step.group)
    mode = aiResponderEnabled && currentKeyword.ai_response?.active ? 'ai' : 'static'

    App.textarea_counter.create_counters()
  })

  let autoResponse = $derived(currentKeyword.auto_response)
  let aiResponse = $derived(currentKeyword.ai_response)
  let mode = $state<'static' | 'ai'>('static')
  let autoResponseCoupon = $derived(couponsStore.couponsByName.find(coupon => autoResponse.target_id === coupon.id))

  // Mode toggle enforces mutual exclusion by deactivating the *other* path.
  // The user explicitly enables the selected mode via that form's enable switch.
  function selectMode(next: 'static' | 'ai') {
    mode = next
    if (next === 'ai') {
      autoResponse.active = false
    } else if (aiResponse) {
      aiResponse.active = false
    }
  }

  let autoResponseTextForDisplay = $derived(autoResponse.target_id ?
    `${autoResponse.text} ${autoResponseCoupon.shortened_url}` :
    autoResponse.text)

  let confirmationTextForDisplay = $derived.by(() => {
    let text = currentKeyword.confirmation_text

    if (sendUpdateLinksStep.active) {
      text += "\nEdit your profile tsig.io/abc123"
    }

    text += currentKeyword.compliance_text

    return text
  })

  let sendFollowUpsStep = $derived(currentKeyword.send_follow_ups)
  let sendUpdateLinksStep = $derived(currentKeyword.send_update_links)
  let followupTextForDisplay = $derived(`${sendFollowUpsStep.text}\ntsig.io/abc123`)
  let isAutoResponder = $derived(String(currentKeyword.keyword_type).match(/auto/i))
  let isTextToDownload = $derived(String(currentKeyword.keyword_type).match(/down/i))
  let updateLinkLength = $derived(sendUpdateLinksStep.active ? 32 : 0)

  function isInvalid(field) {
    return formErrors.includes(field)
  }

  function panelNumber(n) {
    return isAutoResponder ? n : n - 1
  }

  function removeGroup(stepToRemove) {
    selectedGroups = selectedGroups.filter(step => step.id !== stepToRemove.id)
  }

  const validateForm = async () => {
    try {
      formErrors = []
      await keywordSchema.validate(currentKeyword, { abortEarly: false })
      return true
    } catch (err) {
      formErrors = err.errors
      return false
    }
  }

  async function submitForm() {
    globalStore.clearErrors()
    formErrors = []

    await validateForm()

    if (formErrors.length) {
      scrollToTop()
      showErrors()
      return
    }

    await submit()
    // location = '/keywords'
  }

  async function submit() {
    // Make sure we use the same updated keyword in each promise
    let kwd = currentKeyword
    let ar = autoResponse
    let air = aiResponse
    let followUps = sendFollowUpsStep
    let updateLinks = sendUpdateLinksStep
    let newGroups = selectedGroups

    console.log('submitting', {
      kwd: JSON.stringify(kwd),
      ar: JSON.stringify(ar),
      air: JSON.stringify(air),
      followUps: JSON.stringify(followUps),
      updateLinks: JSON.stringify(updateLinks),
      newGroups: JSON.stringify(newGroups)
    })

    window['App'].busy.start()

    const requests = [
      keywordsStore.updateKeyword({
        id: kwd.to_param,
        params: {
          keyword: kwd,
          keyword_type: kwd.keyword_type
        }
      }),
      keywordsStore.updateActionStep({
        id: ar.id,
        params: {
          active: ar.active,
          text: ar.text,
          target_id: ar.target_id
        }
      }),
      ...(aiResponderEnabled && air?.id ? [keywordsStore.updateActionStep({
        id: air.id,
        params: {
          active: air.active,
          text: air.text,
          ai_context: air.ai_context
        }
      })] : []),
      keywordsStore.updateActionStep({
        id: updateLinks.id,
        params: {
          active: updateLinks.active,
        }
      }),
      keywordsStore.updateActionStep({
        id: followUps.id,
        params: {
          active: followUps.active,
          text: followUps.text,
        }
      }),
      keywordsStore.syncAddToGroups({
        keyword_id: kwd.to_param,
        group_ids: newGroups.map(group => group.id)
      })
    ]

    Promise.all(requests)
      .then(responses => {
        // All requests succeeded
        location = '/keywords'
      })
      .catch(error => {
        // At least one request failed
        console.error('One or more requests failed:', error)
        window['App'].busy.done()
      });

  }

  function showErrors() {
    for (var attr in validations.currentKeyword.$params) {
      let param = validations.currentKeyword[attr]
      if (param.$invalid) {
        formErrors.push(`${capitalize(attr)} is not valid`)
      }
    }
  }

  function hideAddGroupsModal() {
    showingAddGroupsModal = false
  }

  function showAddGroupsModal() {
    showingAddGroupsModal = true
  }

</script>

<div class="KeywordsForm keyword-form-edit">
  <Portal target="#cta-portal">
    <button class="btn btn-success btn-rounded" onclick={submitForm}>Save</button>
  </Portal>

  <div class="panel shaded keyword-how-to">
    <p class="keyword-how-to__lead">
      {#if savedKeyword.retain_new_contacts}
        To get contacts to sign up from their phone, tell them:
      {:else}
        To have people get your automatic reply, tell them:
      {/if}
    </p>
    <p class="keyword-how-to__cta">
      Text <strong>{savedKeyword.keyword}</strong> to <strong>{savedKeyword.account.short_code}</strong>
    </p>
    <p class="keyword-how-to__where">
      Share it in person, or put it on posters, slideshows, receipts and social posts.
    </p>
  </div>

  <div class="panel panel--padded shaded keyword-details">
    <div class="panel-heading">
      <span class="panel-number">1</span>
      Choose Your Keyword
    </div>

    <div class="panel-body">
      <GlobalErrors errors={formErrors} />

      <div class="flex-row mt-1">
        <div class="cell-1">
          <div class="form-group">
            <label for="keyword_keyword_type" class="control-label required">Type</label>
            <select bind:value={currentKeyword.keyword_type} class="form-control">
              <option>Autoresponder</option>
              <option>Text to Join</option>
              <option>Text to Download</option>
            </select>
          </div>
        </div>
      </div>

      <div class="flex-row mt-1">
        <div class="cell-1">
          <div class="form-group" class:has-error={isInvalid('keyword')}>
            <div class="keyword-availability js-keyword-availability pull-right">
              <div class="checking">Checking...</div>
              <div class="available">Available</div>
              <div class="not-available">Not available</div>
            </div>

            <label for="keyword_keyword" class="control-label required">Keyword</label>
            <input class="form-control keyword-field js-keyword-field"
                   required
                   autofocus
                   maxlength="16"
                   autocomplete="off"
                   data-id="{currentKeyword.to_param}"
                   type="text"
                   bind:value={currentKeyword.keyword}
            />
            <div class="help-block" class:d-none={!isInvalid('keyword')}>Keyword is required.</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  {#if isAutoResponder || isTextToDownload}
    <div class="divider--blue">
      When a contact texts this keyword, they’ll get your automated response
    </div>

    <div class="panel panel--padded shaded">
      <div class="panel-heading">
        <span class="panel-number">2</span>
        Create Your Custom Auto Response
      </div>

      <div class="panel-body">

        {#if aiResponderEnabled}
          <div class="form-group mt-1">
            <div class="btn-group response-mode-toggle" role="group" aria-label="Response mode">
              <button
                type="button"
                class="btn"
                class:btn-success={mode === 'static'}
                class:btn-default={mode !== 'static'}
                onclick={() => selectMode('static')}
              >Static Response</button>
              <button
                type="button"
                class="btn"
                class:btn-success={mode === 'ai'}
                class:btn-default={mode !== 'ai'}
                onclick={() => selectMode('ai')}
              >AI Responder</button>
            </div>
          </div>
        {/if}

        {#if mode === 'static'}
          <div class="panel--well mt-1">
            <div class="row">
              <div class="col-sm-10">
                <h4>
                  Enable this Auto Response?
                </h4>
                <small class="help-block mb-0">
                  This feature costs one credit per response.
                </small>
              </div>
              <div class="col-sm-2 text-right">
                <label class="block mb-0">
                  <input type="checkbox"
                         class="switch-checkbox"
                         bind:checked={autoResponse.active}
                  />
                  <span class="switch"></span>
                </label>
              </div>
            </div>
          </div>

          <div class="panel--well mt-1">
            <div class="row mt-1">
              <div class="col-sm-6">
                <div class="form-group">
                  <label for="auto-response-textarea">Auto Response Message</label>
                  <LinkifyingTextarea
                    id="auto-response-textarea"
                    classNames="auto-response-textarea"
                    disabled={!autoResponse.active}
                    placeholder="Your custom message"
                    hasImages={false}
                    coupon={autoResponseCoupon}
                    bind:value={autoResponse.text}
                  >
                    <AttachCouponsList bind:value={autoResponse.target_id} coupons={couponsStore.couponsByName} />
                  </LinkifyingTextarea>
                  <div class="help-block">
                    Note: This feature costs one credit per response.
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group text-center">
                  <label for="auto-response-preview">Auto Response Preview</label>

                  <LinkifyingPhonePreview
                    id="auto-response-preview"
                    text={autoResponseTextForDisplay}
                  />
                </div>
              </div>
            </div>
          </div>
        {:else if aiResponse}
          <AiResponderForm bind:aiResponse={currentKeyword.ai_response} />
        {/if}

      </div>
    </div>
  {/if}

  {#if currentKeyword.retain_new_contacts}
    <div class="divider--blue">
      When a contact texts this keyword, they’ll get a confirmation message
    </div>
  {:else}
    <div class="divider--blue">
      Optionally, you can save contacts to your account
    </div>
  {/if}

  <div class="panel panel--padded shaded">
    <div class="panel-heading">
      <span class="panel-number">{panelNumber(3)}</span>
      Choose to Save the Contacts
    </div>

    <div class="panel-body">

      {#if isAutoResponder || isTextToDownload}
        <div class="panel--well mt-1">
          <div class="row">
            <div class="col-sm-10">
              <h4>
                Do you want to save the contacts?
              </h4>
              <p>
                When you save contacts, we also have to send a (customizable) confirmation message.
              </p>
            </div>
            <div class="col-sm-2 text-right">
              <label class="block mb-0">
                <input
                  type="checkbox"
                  class="switch-checkbox"
                  bind:checked={currentKeyword.retain_new_contacts}
                />
                <span class="switch"></span>
              </label>
            </div>
          </div>
        </div>
      {:else}
        <div style="display: none;">
          <input
            type="checkbox"
            bind:checked={currentKeyword.retain_new_contacts}
          />
        </div>
      {/if}

      {#if currentKeyword.retain_new_contacts}
        <div class="panel--well mt-2" transition:fade>
          <h4>
            You can customize (&amp; preview) the message below
          </h4>
          <div class="row mt-2">
            <div class="col-sm-6">
              <div class="form-group">
                <label for="confirmation-message-textarea">Required Message</label>
                <LinkifyingTextarea
                  id="confirmation-message-textarea"
                  classNames="confirmation-message-textarea"
                  disabled={!currentKeyword.retain_new_contacts}
                  placeholder={currentKeyword.default_confirmation_text}
                  numReservedChars={currentKeyword.compliance_text.length + updateLinkLength}
                  bind:value={currentKeyword.confirmation_text}
                >
                </LinkifyingTextarea>

                <div class="text-center">
                  <label class="mt-1">
                    <input type="checkbox" bind:checked={sendUpdateLinksStep.active} />
                    Send contacts a link to update their own info?
                  </label>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group text-center">
                <label for="confirmation-message-preview">Required Message Preview</label>

                <LinkifyingPhonePreview
                  id="confirmation-message-preview"
                  text={confirmationTextForDisplay}
                />
              </div>
            </div>
          </div>
        </div>
      {/if}

    </div>
  </div>


  {#if currentKeyword.retain_new_contacts}
    <div transition:fade>
      <div class="divider--blue">
        Optionally, you can save contacts to a group
      </div>

      <div class="panel panel--padded shaded">
        <div class="panel-heading">
          <span class="panel-number">{panelNumber(4)}</span>
          Group Selection
        </div>

        <div class="panel-body">
          <label for="groups">Groups</label>
          <div class="panel--well">
            <div class="row">
              <div class="col-sm-10">
                <div class="labels-container-flex">
                  {#each selectedGroups as group (group.id)}
                    <div class="label label--blue" transition:fade>
                      <div class="icon icon--group"></div>
                      {group.name}
                      <button
                        type="button"
                        class="btn-link icon--close-x small red"
                        aria-label="Remove"
                        onclick={() => removeGroup(group)}>&nbsp;
                      </button>
                    </div>
                  {/each}
                </div>
              </div>

              <div class="col-sm-2 text-right">
                <button type="button" class="btn btn-link--green nowrap" onclick={showAddGroupsModal}>
                  <small>Add Group</small>
                  <span type="button" class="btn btn-add--round"></span>
                </button>
              </div>
            </div>
          </div>

          <AddToGroupForm
            show={showingAddGroupsModal}
            onClose={hideAddGroupsModal}
            {exclusiveGroups}
            bind:value={selectedGroups} />
        </div>
      </div>

      <div class="divider--blue">
        Optionally, you can follow-up 24-hours later (helps build contact profile)
      </div>

      <div class="panel panel--padded shaded">
        <div class="panel-heading">
          <span class="panel-number">{panelNumber(5)}</span>
          24 Hour Follow Up
        </div>

        <div class="panel-body">

          <div class="panel--well mt-1">
            <div class="row">
              <div class="col-sm-10">
                <h4>
                  Send contacts a 24-hour follow up to update their profile?
                </h4>
              </div>
              <div class="col-sm-2 text-right">
                <label class="block mb-0">
                  <input type="checkbox"
                         class="switch-checkbox"
                         bind:checked={sendFollowUpsStep.active}
                  />
                  <span class="switch"></span>
                </label>
              </div>
            </div>
          </div>

          {#if sendFollowUpsStep.active}
            <div class="panel--well mt-2" transition:fade>
              <div class="row mt-2">
                <div class="col-sm-6">
                  <div class="form-group">
                    <label for="followup-message-textarea">Follow Up Message</label>
                    <LinkifyingTextarea
                      id="followup-message-textarea"
                      classNames="followup-message-textarea"
                      disabled={!sendFollowUpsStep.active}
                      placeholder={sendFollowUpsStep.default_text}
                      numReservedChars="20"
                      bind:value={sendFollowUpsStep.text}
                    />
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="form-group text-center">
                    <label for="followup-message-preview">Follow Up Preview</label>

                    <LinkifyingPhonePreview
                      id="followup-message-preview"
                      text={followupTextForDisplay}
                    />
                  </div>
                </div>
              </div>
            </div>
          {/if}

        </div>
      </div>
    </div>
  {/if}

  <SuperadminAccordion class="mt-2">
    <AutoResponderWidget keyword={currentKeyword} />
    <SkipOptIns keyword={currentKeyword} />
  </SuperadminAccordion>
</div>
